import { PortfolioExportJobInformation } from "@/tools/aggregate/portfolio-export/components/PortfolioExportJobInformation";
import { usePortfolioTutorialsState } from "@/tools/aggregate/portfolio-export/PortfolioTutorialContext";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { getIsAdaptationProcessing } from "@/tools/aggregate/portfolio-export/utils/progress";
import { useLssTutorialState } from "@/tools/risk-bands/large-site-screen/context/LssTutorialContext";
import { Alert, Button, List } from "antd";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
export type PortfolioListState =
// No data - first fetch, or the first decrypt pass.
{
  kind: "loading";
}
// Rows on screen and still current, but newer data is on its way
| {
  kind: "refreshing";
  rows: PortfolioExportJobSchema[];
} | {
  kind: "ready";
  rows: PortfolioExportJobSchema[];
} | {
  kind: "empty";
  reason: "no-data" | "no-results";
};
interface PortfolioExportJobTableBodyProps {
  state: PortfolioListState;
  totalSchemas: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  updateSchema: (schema: PortfolioExportJobSchema) => void;
  onDelete: (id: string) => void;
  onError: (err: string | string[], portfolioId: string) => void;
  onEdit: (props: {
    schema: PortfolioExportJobSchema;
    name: string;
    isPrivate: boolean;
    groupId: string;
  }) => Promise<void>;
  onClearFilters?: () => void;
  canRunAutoAdaptation: boolean;
  availablePortfolioTypes?: string[];
}
export const PortfolioExportJobTableBody = ({
  state,
  totalSchemas,
  currentPage,
  onPageChange,
  pageSize,
  updateSchema,
  onDelete,
  onError,
  onEdit,
  onClearFilters,
  canRunAutoAdaptation,
  availablePortfolioTypes = []
}: PortfolioExportJobTableBodyProps) => {
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.portfolioInsights"
  });
  const rows = state.kind === "ready" || state.kind === "refreshing" ? state.rows : [];
  const isProcessing = rows.filter(({
    status
  }) => ["processing", "pending", "provisioning", "running", "uploading"].includes(status)).length > 0;
  const isAdaptationProcessing = rows.some(schema => getIsAdaptationProcessing(schema, canRunAutoAdaptation));
  const {
    isTutorialOpen,
    tutorialNewRunState,
    tutorialPortfolioSchema,
    analysisRun
  } = usePortfolioTutorialsState();
  const {
    analysisLibrary
  } = useLssTutorialState();
  const showTutorialAnalysisProcessingAlert = isTutorialOpen && tutorialNewRunState === "UPLOADING" && tutorialPortfolioSchema.status === "processing";
  const showTutorialSchemaItem = showTutorialAnalysisProcessingAlert || isTutorialOpen && tutorialNewRunState === "INITIAL" && tutorialPortfolioSchema.status === "completed";
  const containerClass = `mb-4 min-h-36 overflow-hidden rounded-lg ${showTutorialSchemaItem ? "rounded-t-none" : ""} border border-gray-200`;
  return <>
      {(isProcessing || isAdaptationProcessing || showTutorialAnalysisProcessingAlert || showTutorialSchemaItem) && <div ref={analysisRun.alert}>
          {(isProcessing || isAdaptationProcessing || showTutorialAnalysisProcessingAlert) && <Alert message={<div className="text-sm font-medium text-sky-700">{t("processingAlert.title")}</div>} description={<div className="text-xs text-sky-700">{t("processingAlert.blurb")}</div>} rootClassName="border-none p-4 mb-4" />}
          {showTutorialSchemaItem && <div ref={analysisRun.item}>
              <List.Item className="!flex !items-center rounded-lg rounded-b-none border border-b-0 !border-gray-200 !p-4 xl:h-[72px]">
                <PortfolioExportJobInformation onDelete={() => {}} onEdit={onEdit} schema={tutorialPortfolioSchema} updateSchema={() => {}} onError={onError} availablePortfolioTypes={availablePortfolioTypes} canRunAutoAdaptation={canRunAutoAdaptation} isTutorial />
              </List.Item>
            </div>}
        </div>}

      <div ref={analysisLibrary} className="relative overflow-x-auto">
        {state.kind === "loading" && <div className={containerClass} role="status" aria-busy="true" aria-label="Loading analyses">
            {Array.from({
          length: pageSize
        }, (_, index) => <CustomSkeletonRow key={index} index={index} />)}
          </div>}

        {/* The tutorial's stand-in row sits above this list, so it counts as content. */}
        {state.kind === "empty" && !showTutorialSchemaItem && <div className={`${containerClass} flex flex-col items-center justify-center gap-2 px-6 py-12 text-center`}>
            <p className="m-0 text-sm font-medium text-gray-700">
              {state.reason === "no-results" ? "No analyses match these filters" : "No analyses yet"}
            </p>
            <p className="m-0 text-sm text-gray-500">
              {state.reason === "no-results" ? "Try a different user, group or portfolio type." : "Use Start New Run above to begin your first analysis."}
            </p>
            {state.reason === "no-results" && onClearFilters && <Button type="link" className="p-0" onClick={onClearFilters}>
                Clear filters
              </Button>}
          </div>}

        {(state.kind === "ready" || state.kind === "refreshing") && <List dataSource={rows} itemLayout="vertical" pagination={{
        pageSize,
        total: totalSchemas,
        onChange: onPageChange,
        showSizeChanger: false,
        className: "mb-4 mr-5 [&_a]:!no-underline",
        current: currentPage,
        // Paging again mid-refresh would race the request already in flight.
        disabled: state.kind === "refreshing"
      }} rowKey="id"
      // Rows stay put while newer data loads; dimming is the whole indicator.
      aria-busy={state.kind === "refreshing"} className={`${containerClass} transition-opacity motion-reduce:transition-none [&>a]:!no-underline ${state.kind === "refreshing" ? "opacity-60" : ""}`} size="large" renderItem={schema => <List.Item className="!flex !items-center border-0 border-b !border-gray-200 !p-4 odd:bg-gray-50 xl:h-[72px]">
                <PortfolioExportJobInformation onDelete={() => onDelete(schema.id)} onEdit={onEdit} schema={schema} updateSchema={updateSchema} onError={onError} availablePortfolioTypes={availablePortfolioTypes} canRunAutoAdaptation={canRunAutoAdaptation} />
              </List.Item>} />}
      </div>
    </>;
};
const SkeletonBar = ({
  className = "",
  style
}: {
  className?: string;
  style?: CSSProperties;
}) => <div className={`animate-pulse rounded bg-gray-200 motion-reduce:animate-none ${className}`} style={style} data-sentry-component="SkeletonBar" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />;
const CustomSkeletonRow = ({
  index = 0
}: {
  index?: number;
}) => {
  const NAME_WIDTHS = ["40%", "50%", "45%", "60%", "45%"];
  const META_WIDTHS = ["65%", "70%", "60%", "70%", "75%"];
  return <div aria-hidden className="flex min-h-[72px] items-center justify-between gap-4 border-0 border-b !border-gray-200 p-4 odd:bg-gray-50 xl:h-[72px]" data-sentry-component="CustomSkeletonRow" data-sentry-source-file="PortfolioExportJobTableBody.tsx">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {/* Title line: status dot, name, status pill. */}
        <div className="flex items-center gap-2">
          <SkeletonBar className="h-4 w-4 shrink-0 rounded-full" data-sentry-element="SkeletonBar" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
          <SkeletonBar className="h-4" style={{
          width: NAME_WIDTHS[index % NAME_WIDTHS.length]
        }} data-sentry-element="SkeletonBar" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
          <SkeletonBar className="h-5 w-16 shrink-0 rounded-full" data-sentry-element="SkeletonBar" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
        </div>

        {/* Metadata line */}
        <SkeletonBar className="h-3" style={{
        width: META_WIDTHS[index % META_WIDTHS.length]
      }} data-sentry-element="SkeletonBar" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
      </div>

      {/* Actions on the right */}
      <div className="flex shrink-0 items-center gap-2">
        <SkeletonBar className="hidden h-8 w-24 sm:block" data-sentry-element="SkeletonBar" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
        <SkeletonBar className="hidden h-8 w-24 md:block" data-sentry-element="SkeletonBar" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
        <SkeletonBar className="h-8 w-8" data-sentry-element="SkeletonBar" data-sentry-source-file="PortfolioExportJobTableBody.tsx" />
      </div>
    </div>;
};